/**
 * Utility functions for interacting with product API
 */

import axios from 'axios';

// API base URL - use the actual backend URL without the /api prefix
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

/**
 * Search for products by supplement key
 * 
 * @param {string} supplementKey - The supplement key
 * @param {object} options - Additional search options (sorting, filtering, etc.)
 * @returns {Promise<Array>} - Promise resolving to an array of product objects
 */
const searchProductsForSupplement = async (supplementKey, options = {}) => {
  try {
    if (!supplementKey) {
      console.error('Invalid supplement key');
      return [];
    }
    
    // Build query parameters
    const params = new URLSearchParams({
      key: supplementKey,
      ...options
    });
    
    // Make API request
    const response = await axios.get(`${API_BASE_URL}/products/supplement?${params.toString()}`);
    return response.data.products || [];
  } catch (error) {
    console.error('Error fetching products:', error);
    return [];
  }
};

/**
 * Find most researched supplements
 * 
 * @param {object} options - Options like count
 * @returns {Promise<Object>} - Promise resolving to an object with supplements array
 */
const findSupplements = async (options = {}) => {
  try {
    // Build query parameters
    const params = new URLSearchParams(options);
    
    // Make API request
    const response = await axios.get(`${API_BASE_URL}/supplements/find?${params.toString()}`);
    return response.data || { supplements: [], total: 0 };
  } catch (error) {
    console.error('Error finding supplements:', error);
    return { supplements: [], total: 0 };
  }
};

/**
 * Search for Amazon products (alias for searchProductsForSupplement for backward compatibility)
 * 
 * @param {string} searchTermOrKey - The supplement key (should be taken directly from supplements.json)
 * @param {object} options - Additional search options (sorting, filtering, etc.)
 * @returns {Promise<Array>} - Promise resolving to an array of product objects
 */
const searchAmazonProducts = async (searchTermOrKey, options = {}) => {
  // DEPRECATED: This function is kept for backward compatibility
  // It's recommended to use searchProductsForSupplement with the exact supplement key from the data source
  console.warn('searchAmazonProducts is deprecated. Use searchProductsForSupplement with the exact supplement key instead.');
  
  // Use the key directly without manipulation
  return searchProductsForSupplement(searchTermOrKey, options);
};

/**
 * Get product details by ID
 * 
 * @param {string} productId - The product ID
 * @returns {Promise<object>} - Promise resolving to product details
 */
const getProductById = async (productId) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/products/${productId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching product details:', error);
    return null;
  }
};

/**
 * Build an affiliate link for a product
 * 
 * @param {string} productUrl - The original product URL
 * @returns {string} - The affiliate link
 */
const buildAffiliateLink = (productUrl) => {
  if (!productUrl) return '';
  
  try {
    const url = new URL(productUrl);
    // Add affiliate tag (example for Amazon)
    url.searchParams.append('tag', 'youraffiliatetag-20');
    return url.toString();
  } catch (error) {
    console.error('Error building affiliate link:', error);
    return productUrl;
  }
};

export {
  searchProductsForSupplement,
  searchAmazonProducts,
  getProductById,
  buildAffiliateLink,
  findSupplements
}; 